import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, LinearProgress, Fade } from '@mui/material';
import QuizResults from '@/components/ui/QuizResults/QuizResults';
import { useLesson } from '@/api/useLessons';
import { useGenerateQuiz, QuizQuestion } from '@/api/useGenerateQuiz';

const QuizPage: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { data: lesson, isLoading: lessonLoading } = useLesson(lessonId);
  const generateQuiz = useGenerateQuiz();

  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [score, setScore] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<{
    questionId: number;
    isCorrect: boolean;
    category?: string;
  }[]>([]);

  useEffect(() => {
    if (questions.length > 0 || generateQuiz.isPending || generateQuiz.isSuccess || generateQuiz.isError) return;

    if (lesson?.content) {
      generateQuiz.mutate(
        {
          title: lesson.title,
          content: lesson.content,
          subject: lesson.subject,
          language: lesson.language,
        },
        { onSuccess: (data) => setQuestions(data) }
      );
    }
  }, [lesson]);

  if (!lessonLoading && !lesson) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6">
        <p className="text-gray-500 font-semibold">No content available for quiz</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-6 py-3 bg-[#6B8BF5] text-white rounded-full font-semibold"
        >
          Go back
        </button>
      </div>
    );
  }

  const isLoadingQuiz = lessonLoading || generateQuiz.isPending || questions.length === 0;

  if (generateQuiz.isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6">
        <p className="text-red-500 font-semibold">Failed to generate quiz</p>
        <p className="text-gray-500 text-sm text-center">{generateQuiz.error?.message}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-6 py-3 bg-[#6B8BF5] text-white rounded-full font-semibold"
        >
          Go back
        </button>
      </div>
    );
  }

  if (isLoadingQuiz) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-6">
        <div className="w-12 h-12 border-4 border-[#6B8BF5] border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-600 font-medium text-lg">Generating quiz questions...</p>
        <p className="text-gray-400 text-sm">AI is creating questions based on your lesson</p>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  const handleNext = () => {
    if (selectedAnswer) {
      const answerIndex = parseInt(selectedAnswer);
      let newScore = score;
      const isCorrect = answerIndex === currentQuestion.correctAnswer;

      if (isCorrect) {
        newScore = score + 1;
        setScore(newScore);
      }

      setAnsweredQuestions([...answeredQuestions, {
        questionId: currentQuestion.id,
        isCorrect,
        category: currentQuestion.category
      }]);

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedAnswer('');
      } else {
        setIsCompleted(true);
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getOptionLetter = (index: number): string => {
    return String.fromCharCode(65 + index);
  };

  const analyzeResults = () => {
    const categoryResults = answeredQuestions.reduce((acc, answer) => {
      if (!answer.category) return acc;

      if (!acc[answer.category]) {
        acc[answer.category] = { correct: 0, total: 0 };
      }

      acc[answer.category].total++;
      if (answer.isCorrect) {
        acc[answer.category].correct++;
      }

      return acc;
    }, {} as Record<string, { correct: number; total: number }>);

    const strongAreas: string[] = [];
    const areasForImprovement: string[] = [];

    Object.entries(categoryResults).forEach(([category, results]) => {
      const percentage = (results.correct / results.total) * 100;
      const displayName = category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      if (percentage >= 70) {
        strongAreas.push(`You demonstrated a strong understanding of ${displayName}.`);
      } else {
        areasForImprovement.push(`Review and practice more with ${displayName} topics.`);
      }
    });

    if (strongAreas.length > 0) {
      strongAreas.push('Keep up the great work and continue practicing to reinforce your knowledge.');
    }

    if (areasForImprovement.length === 0) {
      areasForImprovement.push('Excellent performance! Try more challenging material to further improve.');
    }

    return { strongAreas, areasForImprovement };
  };

  const quizTitle = lesson?.title ?? 'Quiz';

  const content = isCompleted ? (
    <QuizResults
      score={score}
      totalQuestions={questions.length}
      title={quizTitle}
      {...analyzeResults()}
    />
  ) : (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <button onClick={handleBack} className="w-10 h-10 flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M15 10C15 10.4142 14.6642 10.75 14.25 10.75L7.61208 10.75L9.76983 12.7094C10.0684 12.9965 10.0777 13.4713 9.79062 13.7698C9.50353 14.0684 9.02875 14.0777 8.73017 13.7906L5.23017 10.5406C5.08311 10.3992 5 10.204 5 10C5 9.79599 5.08311 9.60078 5.23017 9.45938L8.73017 6.20938C9.02875 5.92228 9.50353 5.93159 9.79062 6.23017C10.0777 6.52875 10.0684 7.00353 9.76983 7.29063L7.61208 9.25L14.25 9.25C14.6642 9.25 15 9.58579 15 10Z" fill="#1F2937"/>
          </svg>
        </button>
        <div className="bg-[#6B8BF5] text-white px-6 py-2 rounded-full font-medium text-base">
          {quizTitle} - Quiz
        </div>
      </div>
      <div className="mb-6">
        <Typography variant="body2" sx={{ color: '#999', mb: 1 }}>
          Progress tracking - {currentQuestionIndex + 1}/{questions.length}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 10,
            borderRadius: 4,
            bgcolor: '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#FF6B6B',
              borderRadius: 4,
            },
          }}
        />
      </div>

      <Fade in={true} timeout={500}>
        <div className="flex flex-col gap-6">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
            {currentQuestion.question}
          </Typography>

          <div className="flex flex-col gap-3">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => setSelectedAnswer(index.toString())}
                className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
                  selectedAnswer === index.toString()
                    ? 'border-[#6B8BF5] bg-[#6B8BF5] bg-opacity-5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    selectedAnswer === index.toString()
                      ? 'bg-[#6B8BF5] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {getOptionLetter(index)}
                </div>
                <span className={`flex-1 text-left ${
                  selectedAnswer === index.toString() ? 'text-[#6B8BF5]' : 'text-gray-700'
                }`}>
                  {option}
                </span>
              </button>
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={!selectedAnswer}
            className={`mt-4 w-full py-3 px-6 rounded-full font-semibold text-white transition-all ${
              selectedAnswer
                ? 'bg-[#FF6B6B] hover:bg-[#FF5252]'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </Fade>
    </div>
  );

  return content;
};

export default QuizPage;
